import "react-markdown-editor-lite/lib/index.css";

import MarkdownIt from "markdown-it";
import emoji from "markdown-it-emoji";
import React, { useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import slackifyMarkdown from "slackify-markdown";

import { getChannels, getUsersPaginated } from "../../lib/queries";
import QuillEditor from "./QuillEditor";

const mdParser = new MarkdownIt();
mdParser.use(emoji);

type onChangeProps = {
  [key: string]: string;
  html: string;
  text: string;
  slack_mrkdwn: string;
};

type Props = {
  onChange: (obj: onChangeProps) => void; // The handler for the onChange event
  value: any; // A message state object from parent.
  disablePersonalTokens: boolean; // Disable personalization tokens (e.g. {{first_name}})
  templateText?: string; // the template text to be inserted
};

/**
 * Component that acts as a wrapper for QuillEditor. Fetches channels and members
 * @param props @see Props
 * @returns
 */
const SlackEditor = (props: Props) => {
  type Member = {
    text: string;
    value: string;
    id: string;
    avatar: string;
  };
  type Channel = {
    text: string;
    value: string;
    id: string;
  };

  const teamId = localStorage.getItem("team-id");
  const [filterList, setFilterList] = useState<Member[]>([]);
  const [channelsList, setChannelsList] = useState<Channel[]>([]);

  function handleEditorChange({ html, text }: { html: string; text: string }) {
    const slackMrkdwn = slackifyMarkdown(text);
    props.onChange({
      ...(typeof props.value === "object" ? props.value : {}),
      html,
      text,
      slack_mrkdwn: slackMrkdwn,
    });
  }

  /**
   * Load all users for the given team, parsed into {id: string, value: string} format.
   */
  const loadUsersList = async () => {
    const data = await getUsersPaginated(teamId);
    const usersNameArray: Member[] = [];
    data.forEach((item: any) => {
      const obj: Member = {
        text: item.name,
        value: item.name,
        id: item.slack_id,
        avatar: item.image_32,
      };
      usersNameArray.push(obj);
    });
    setFilterList(usersNameArray);
  };

  /**
   * Load all channels for the given team, parsed into {id: string, value: string} format.
   */
  const loadChannelsList = async () => {
    const data = await getChannels(teamId);
    const channelsNameArray: Channel[] = [];
    if (data) {
      data.forEach((item: any) => {
        const obj: Channel = {
          text: item.name,
          value: item.name,
          id: item.slack_id,
        };
        channelsNameArray.push(obj);
      });
    }
    setChannelsList(channelsNameArray);
  };

  useEffect(() => {
    loadUsersList();
    loadChannelsList();
  }, []);

  return (
    <div style={{ width: "100%" }}>
      <QuillEditor
        members={filterList}
        channels={channelsList}
        onChange={handleEditorChange}
        value={props.value?.html || ""}
        templateText={props.templateText}
        disablePersonalTokens={props.disablePersonalTokens}
        personalizationValues={[
          { id: "@_mention", value: "@_mention}}" },
          { id: "first_name", value: "first_name}}" },
        ]}
        platform="slack"
      />
    </div>
  );
};

// Slack Editor Preview component
type EditorPreviewProps = {
  message: { html: string }; // an object containing html generated by the DiscordEditor
};

/**
 * Display editor's HTML value in a read only "preview"
 * @param props @see EditorPreviewProps
 * @returns component
 */
const SlackEditorPreview = (props: EditorPreviewProps) => {
  const modules = {
    toolbar: false,
  };
  return (
    <div className="quill-wrapper">
      <ReactQuill
        theme="snow"
        value={props.message.html}
        readOnly
        modules={modules}
      />
    </div>
  );
};
export { SlackEditor, SlackEditorPreview };
